///
/// EarthBrowser
///
/// Created by Matt Giger on 1/3/2026
/// Copyright (c) 2026 Geodata Labs
///

import { addListener } from "@reduxjs/toolkit"
import { appStore } from "./store"

type ActionCallback<T = unknown> = (payload: T) => void

/**
 * ReduxActionBridge - Subscribe to specific Redux actions from non-React code
 *
 * This provides a cleaner alternative to the catch-all event dispatch pattern,
 * allowing engine code to subscribe directly to specific Redux action types.
 *
 * Usage:
 * ```ts
 * import { reduxActionBridge } from "@/redux/ReduxActionBridge"
 * import { setCameraBearing } from "@/redux/earthBrowserSlice"
 *
 * // Subscribe to a specific action
 * const unsubscribe = reduxActionBridge.subscribe(
 *   setCameraBearing.type,
 *   (payload: number) => {
 *     focusManager.sendCommand("camera/setBearingImmediate", payload)
 *   }
 * )
 *
 * // Later, clean up
 * unsubscribe()
 * ```
 */
class ReduxActionBridgeClass {
	private subscriptions = new Map<string, Set<ActionCallback>>()
	private listenerUnsubscribe?: () => void
	private isListenerActive = false

	/**
	 * Subscribe to a specific Redux action type
	 * @param actionType The full action type string (e.g., "earthbrowser/setCameraBearing")
	 * @param callback Function called with the action payload when the action is dispatched
	 * @returns Unsubscribe function
	 */
	subscribe<T = unknown>(actionType: string, callback: ActionCallback<T>): () => void {
		// Ensure the listener is active
		this.ensureListenerActive()

		// Add to subscriptions
		if (!this.subscriptions.has(actionType)) {
			this.subscriptions.set(actionType, new Set())
		}
		this.subscriptions.get(actionType)!.add(callback as ActionCallback)

		// Return unsubscribe function
		return () => {
			const callbacks = this.subscriptions.get(actionType)
			if (callbacks) {
				callbacks.delete(callback as ActionCallback)
				if (callbacks.size === 0) {
					this.subscriptions.delete(actionType)
				}
			}

			// Deactivate listener if no more subscriptions
			if (this.subscriptions.size === 0) {
				this.deactivateListener()
			}
		}
	}

	/**
	 * Check if there are any active subscriptions for an action type
	 */
	hasSubscriptions(actionType: string): boolean {
		return this.subscriptions.has(actionType) && this.subscriptions.get(actionType)!.size > 0
	}

	/**
	 * Get count of all active subscriptions
	 */
	get subscriptionCount(): number {
		let count = 0
		this.subscriptions.forEach(callbacks => count += callbacks.size)
		return count
	}

	private ensureListenerActive(): void {
		if (this.isListenerActive) return

		// Add a Redux listener that routes to our subscribers
		// dispatch(addListener(...)) returns an unsubscribe function directly
		this.listenerUnsubscribe = appStore.dispatch(addListener({
			predicate: (action) => {
				// Only match actions we have subscribers for
				return typeof action === "object" &&
					action !== null &&
					"type" in action &&
					this.subscriptions.has(action.type)
			},
			effect: (action) => {
				const actionType = (action as { type: string }).type
				const payload = (action as { payload?: unknown }).payload
				const callbacks = this.subscriptions.get(actionType)

				if (callbacks) {
					callbacks.forEach(callback => {
						try {
							callback(payload)
						} catch (error) {
							console.error(`[ReduxActionBridge] Error in callback for ${actionType}:`, error)
						}
					})
				}
			}
		}))
		this.isListenerActive = true
	}

	private deactivateListener(): void {
		if (this.listenerUnsubscribe) {
			this.listenerUnsubscribe()
			this.listenerUnsubscribe = undefined
		}
		this.isListenerActive = false
	}

	/**
	 * Clear all subscriptions (useful for testing or cleanup)
	 */
	clear(): void {
		this.subscriptions.clear()
		this.deactivateListener()
	}
}

// Export singleton instance
export const reduxActionBridge = new ReduxActionBridgeClass()

